import { ReactNode, useState } from 'react';
import { ChevronDown, Menu, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import SeoHead from '../SeoHead';
import { SOLUTIONS } from '../../data/solutions';

export default function PublicLayout({ children }: { children: ReactNode }) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const getSeoContent = (pathname: string) => {
    if (pathname === '/') {
      return {
        title: 'Studio web, mobile, SaaS et desktop',
        description: 'Ahime cree des plateformes web, SaaS, applications mobiles et logiciels desktop pour Windows, macOS, Linux et Android.',
      };
    }
    if (pathname === '/services') {
      return {
        title: 'Expertises web, mobile, SaaS et desktop',
        description: 'Decouvrez nos expertises en applications web, mobiles, SaaS, desktop, Linux, APIs et cloud.',
      };
    }
    if (pathname === '/solutions') {
      return {
        title: 'Solutions digitales Ahime',
        description: 'Decouvrez Hin, Su POS, WiFiTche et Ajo, les produits digitaux crees par Ahime.',
      };
    }
    if (pathname.startsWith('/solutions/')) {
      return {
        title: 'Detail de solution Ahime',
        description: 'Presentation, plateformes et telechargements des solutions Ahime.',
      };
    }
    if (pathname.startsWith('/services/')) {
      return {
        title: 'Detail de service',
        description: 'Detail de nos offres tech et digitales adaptees a vos objectifs.',
      };
    }
    if (pathname === '/realisations' || pathname.startsWith('/realisations/')) {
      return {
        title: 'Realisations et cas clients',
        description: 'Explorez les projets livres par Ahime pour des clients en Europe et en Afrique.',
      };
    }
    if (pathname === '/blog') {
      return {
        title: 'Blog et insights',
        description: 'Conseils, retours d experience et tendances autour du digital, du produit et de la tech.',
      };
    }
    if (pathname.startsWith('/blog/')) {
      return {
        title: 'Article de blog',
        description: 'Analyse et retours d experience publies par l equipe Ahime.',
      };
    }
    if (pathname === '/contact') {
      return {
        title: 'Contact et lancement de projet',
        description: 'Parlez-nous de votre projet pour demarrer une collaboration rapide et structuree.',
      };
    }
    if (pathname === '/a-propos') {
      return {
        title: 'A propos de Ahime',
        description: 'Notre vision, notre methode et notre equipe au service de votre croissance digitale.',
      };
    }
    if (pathname === '/mentions-legales') {
      return {
        title: 'Mentions legales',
        description: 'Consultez les informations legales et la politique de conformite du site Ahime.',
      };
    }

    return {
      title: 'Ahime',
      description: 'Agence digitale specialisee en produits web sur-mesure.',
    };
  };

  const seo = getSeoContent(location.pathname);
  const isHomePage = location.pathname === '/';

  return (
    <div className="min-h-screen flex flex-col font-sans bg-white">
      <SeoHead
        title={seo.title}
        description={seo.description}
        path={location.pathname}
        noIndex={location.pathname === '/login' || location.pathname === '/register-client'}
        structuredData={
          isHomePage
            ? {
                '@context': 'https://schema.org',
                '@type': 'Service',
                name: 'Studio web, mobile, SaaS et desktop',
                provider: {
                  '@type': 'Organization',
                  name: 'Ahime',
                },
                serviceType: 'Developpement web, SaaS, applications mobiles, applications desktop, Linux, APIs et cloud',
                areaServed: ['Europe', 'Afrique'],
              }
            : undefined
        }
      />
      <header className="sticky top-0 z-50 w-full border-b border-slate-100 bg-white/80 backdrop-blur-md">
        <div className="container mx-auto px-4 sm:px-6 h-20 flex items-center justify-between gap-3">
          <Link to="/" className="inline-flex items-center shrink-0">
            <img src="/logo.png" alt="Ahime" width={900} height={255} decoding="async" className="block h-10 w-auto object-contain" />
          </Link>

          <nav className="hidden md:flex items-center gap-8 font-medium text-slate-600">
            <Link to="/services" className="hover:text-red-600 transition-colors">Expertises</Link>
            <div className="group relative">
              <Link to="/solutions" className="inline-flex items-center gap-1 hover:text-red-600 transition-colors">
                Nos solutions <ChevronDown className="h-4 w-4 transition-transform group-hover:rotate-180" />
              </Link>
              <div className="invisible absolute left-0 top-full z-50 mt-3 w-72 translate-y-2 rounded-3xl border border-slate-100 bg-white p-3 opacity-0 shadow-2xl shadow-slate-900/10 transition-all group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
                {SOLUTIONS.map((solution) => (
                  <Link
                    key={solution.slug}
                    to={`/solutions/${solution.slug}`}
                    className="block rounded-2xl px-4 py-3 transition-colors hover:bg-slate-50"
                  >
                    <span className="block text-sm font-black text-slate-900">{solution.name}</span>
                    <span className="mt-1 block text-xs font-semibold text-slate-500">{solution.tagline}</span>
                  </Link>
                ))}
              </div>
            </div>
            <Link to="/realisations" className="hover:text-red-600 transition-colors">Realisations</Link>
            <Link to="/blog" className="hover:text-red-600 transition-colors">Insights</Link>
            <Link to="/a-propos" className="hover:text-red-600 transition-colors">L'Agence</Link>
          </nav>

          <div className="hidden md:flex items-center gap-4">
            <Link to="/register-client" className="text-sm font-semibold text-slate-900 hover:text-red-600 transition-colors">
              Inscription
            </Link>
            <Link to="/login" className="text-sm font-semibold text-slate-900 hover:text-red-600 transition-colors">
              Connexion
            </Link>
            <Link to="/contact" className="bg-red-600 text-white px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-red-700 transition-all hover:scale-105">
              Demarrer un projet
            </Link>
          </div>

          <div className="flex md:hidden items-center gap-3">
            <Link to="/login" className="text-sm font-semibold text-slate-900 hover:text-red-600 transition-colors">
              Connexion
            </Link>
            <button
              type="button"
              onClick={() => setIsMobileMenuOpen((v) => !v)}
              className="inline-flex items-center justify-center rounded-md p-2 text-slate-700 hover:bg-slate-100"
              aria-label={isMobileMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-slate-100 bg-white">
            <div className="container mx-auto px-4 py-4 flex flex-col gap-4 text-slate-700 font-medium">
              <Link to="/services" className="hover:text-red-600 transition-colors" onClick={() => setIsMobileMenuOpen(false)}>Expertises</Link>
              <Link to="/solutions" className="hover:text-red-600 transition-colors" onClick={() => setIsMobileMenuOpen(false)}>Nos solutions</Link>
              <div className="grid grid-cols-2 gap-2">
                {SOLUTIONS.map((solution) => (
                  <Link
                    key={solution.slug}
                    to={`/solutions/${solution.slug}`}
                    className="rounded-2xl bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-700"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {solution.name}
                  </Link>
                ))}
              </div>
              <Link to="/realisations" className="hover:text-red-600 transition-colors" onClick={() => setIsMobileMenuOpen(false)}>Realisations</Link>
              <Link to="/blog" className="hover:text-red-600 transition-colors" onClick={() => setIsMobileMenuOpen(false)}>Insights</Link>
              <Link to="/a-propos" className="hover:text-red-600 transition-colors" onClick={() => setIsMobileMenuOpen(false)}>L'Agence</Link>
              <Link
                to="/contact"
                onClick={() => setIsMobileMenuOpen(false)}
                className="mt-2 bg-red-600 text-white px-4 py-2.5 rounded-full text-sm font-semibold text-center hover:bg-red-700 transition-colors"
              >
                Demarrer un projet
              </Link>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="bg-slate-900 text-slate-300 py-16 border-t border-slate-800 rounded-t-[3rem] mt-24">
        <div className="container mx-auto px-6 grid grid-cols-1 md:grid-cols-5 gap-8">
          <div>
            <img src="/logo.png" alt="Ahime" width={900} height={255} loading="lazy" decoding="async" className="block h-12 w-auto object-contain mb-4 brightness-0 invert" />
            <p className="text-sm text-slate-300">Studio produit logiciel: web, mobile, SaaS, desktop, Linux et cloud.</p>
          </div>
          <div>
            <h2 className="sr-only">Navigation du pied de page</h2>
            <h3 className="text-white font-semibold mb-4">Expertises</h3>
            <ul className="space-y-2 text-sm text-slate-300">
              <li>Applications web & mobiles</li>
              <li>SaaS & plateformes metier</li>
              <li>Desktop, Linux & systemes</li>
            </ul>
          </div>
          <div>
            <h3 className="text-white font-semibold mb-4">Solutions</h3>
            <ul className="space-y-2 text-sm text-slate-300">
              {SOLUTIONS.map((solution) => (
                <li key={solution.slug}>
                  <Link to={`/solutions/${solution.slug}`} className="hover:text-white transition-colors">
                    {solution.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="text-white font-semibold mb-4">Portails</h3>
            <ul className="space-y-2 text-sm text-slate-300">
              <li>Espace Client B2B</li>
              <li>Espace Partenaire</li>
              <li>Carrieres</li>
            </ul>
          </div>
          <div>
            <h3 className="text-white font-semibold mb-4">Contact</h3>
            <ul className="space-y-2 text-sm text-slate-300">
              <li>Pret a transformer votre ecosysteme ?</li>
              <li>
                <Link to="/mentions-legales" className="text-sm text-slate-300 hover:text-white transition-colors">
                  Mentions legales
                </Link>
              </li>
              <li>
                <Link to="/contact" className="inline-block border border-slate-700 hover:border-white text-white px-4 py-2 rounded-full text-sm transition-colors">
                  Nous contacter
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </footer>
    </div>
  );
}
